<script setup lang="ts">
import { breakpointsTailwind, useBreakpoints } from "@vueuse/core";
import { AUTH_METHOD } from "~/constants";
import useAsync from "~/hooks/use-async";
import AuthAPI from "~/services/auth";
import { base64ToString } from "~/utils/base64";
import { onDeviceLimitError } from "~/utils/device";
import getErrorLocalePathMessage from "~/utils/locale";
import { sleep } from "~/utils/sleep";
import V from "~/utils/validate";

const { t, te } = useI18n();
const route = useRoute();
const router = useRouter();
const appConfig = useRuntimeConfig().public;

const formData = reactive({
  username: (route.query.email as string) || "",
  password: "",
});
type State = typeof formData;

function validate(data: State): IFormError[] {
  const errs: IFormError[] = [];
  const state = unref(data);

  if (!V.required(state.username)) {
    errs.push({ path: "username", message: t("errors.field_is_required") });
  }
  else if (!V.email(state.username)) {
    errs.push({ path: "username", message: t("errors.invalid_email") });
  }

  if (!V.required(state.password)) {
    errs.push({ path: "password", message: t("errors.field_is_required") });
  }
  else if (!V.min(state.password, { length: 8 })) {
    errs.push({ path: "password", message: t("errors.invalid_password") });
  }

  return errs;
}
const hasIfaAuth = appConfig.APP_FLAGS.hasIfaAuth;

const onSuccess = async () => {
  if (hasIfaAuth) {
    window.location.href = "/";
    return;
  }
  router.push((route.query.redirectUrl as string) || "/");
};

const onError = (error: Error) => {
  const err = error as Error & { code?: string; token: string; provider: TAuthProvider };

  if (err.code === "device_limit_exceeded") {
    onDeviceLimitError(
      err.token,
      err.provider,
      { username: formData.username, password: formData.password },
    );
  }
};
const {
  loading: loginLoading,
  error,
  run: executeLogin,
} = useAsync(AuthAPI.signin, { onSuccess, onError });
const {
  loading: socialLoginLoading,
  error: socialError,
  run: executeSocialLogin,
} = useAsync(AuthAPI.signinWithProvider, { onSuccess, onError });

const loading = computed(() => loginLoading.value || socialLoginLoading.value);
const onSubmit = () => {
  if (route.query.sessionExpired || route.query.deviceNoLongerAllowedError) {
    router.replace({
      query: {
        ...route.query,
        sessionExpired: undefined,
        deviceNoLongerAllowedError: undefined,
      },
    });
  }

  return executeLogin(formData.username, formData.password, AUTH_METHOD.SIGN_IN);
};

async function onPasswordResetSuccess() {
  await sleep(5000);

  route.query.passwordResetSuccess
  && router.push({ query: { ...route.query, passwordResetSuccess: undefined } });
}

async function onAutoLogin() {
  if (!route.query.data)
    return;

  try {
    const data = JSON.parse(base64ToString(route.query.data as string));
    if (data.provider === "credentials") {
      formData.username = data.username;
      formData.password = data.password;
      onSubmit();
    }
    else {
      executeSocialLogin(data.provider);
    }
  }
  catch (err: any) {
    error.value = err && err.message;
  }
}

const errorMessage = computed(() => {
  if (route.query.deviceNoLongerAllowedError)
    return t("pages.auth_login.device_no_longer_allowed_error");

  if (route.query.sessionExpired)
    return t("errors.expired_session_error");

  if (error.value)
    return getErrorLocalePathMessage(error.value, { t, te }).message;

  if (socialError.value)
    return getErrorLocalePathMessage(socialError.value, { t, te }).message;

  return null;
});
const breakpoints = useBreakpoints(breakpointsTailwind);
const sm = breakpoints.greaterOrEqual("sm");

onMounted(() => {
  onPasswordResetSuccess();
  onAutoLogin();
});

useHead({ title: computed(() => t("labels.sign_in")) });
definePageMeta({ layout: "auth" });
</script>

<template>
  <AuthHeader :mode="hasIfaAuth ? undefined : 'sign-up'" :fixed="!sm" :absolute="sm" transparent class="my-2 sm:my-0" with-locale />

  <BaseMain>
    <div class="flex flex-col items-center justify-center h-screen sm:pt-0 px-4 sm:px-0">
      <div class="w-full max-w-[25rem]">
        <h1 class="whitespace-pre-wrap text-heading-1-bold text-center sm:text-left">
          {{ t('pages.auth_sign_in.welcome_message') }}
        </h1>

        <template v-if="hasIfaAuth">
          <p class="mt-4 text-caption-medium text-secondary">
            {{ t("pages.auth_sign_in.sign_in_with_provider", { provider: 'IFA' }) }}
          </p>
          <BaseAlert
            v-if="errorMessage"
            type="danger"
            class="mt-6"
            :text="errorMessage"
          />

          <BaseButton
            style="--base-primary-accent-color: #2c374b"
            class="relative mt-8 text-button-bold"
            name="ifa"
            full-width
            :disabled="socialLoginLoading"
            @click="executeSocialLogin('ifa')"
          >
            <BaseImage
              class="absolute top-2.5 left-3" src="/images/icons/icon-ifa-logo.webp"
              fallback-src="/images/mock/logo.svg" alt="Logo" :width="24" :height="24"
            />
            {{ t('labels.continue_with', { provider: 'IFA' }) }}
          </BaseButton>
        </template>

        <template v-else>
          <p class="mt-4 text-caption-medium text-secondary text-center sm:text-left">
            {{ t("pages.auth_sign_up.enter_your_details_below") }}
          </p>

          <BaseAlert
            v-if="errorMessage"
            type="danger"
            class="mt-6"
            :text="errorMessage"
          />
          <BaseAlert
            v-if="route.query.passwordResetSuccess"
            type="success"
            class="mt-6"
            :text="t('pages.auth_sign_up.password_updated_successuly_text')"
          />

          <UForm :state="formData" :validate="validate" class="mt-8" @submit="onSubmit">
            <div class="flex flex-col gap-2 sm:gap-6">
              <UFormGroup
                :label="t('labels.email')"
                name="username"
                required
              >
                <BaseInput
                  v-model="formData.username"
                  :disabled="loading"
                  name="email"
                  :placeholder="t('labels.email_placeholder')"
                  autocomplete="email"
                />
              </UFormGroup>

              <UFormGroup
                :label="t('labels.password')"
                required
                name="password"
              >
                <BaseInput
                  v-model="formData.password"
                  :disabled="loading"
                  :placeholder="t('labels.min_8_characters')"
                  name="password"
                  autocomplete="new-password"
                  sensitive
                />
              </UFormGroup>
            </div>

            <div class="mt-2 flex flex-col gap-8">
              <BaseButton
                name="forgot-password"
                tag="nuxt-link"
                class="text-sm"
                :to="{
                  path: '/auth/password/forgot/',
                  query: { email: formData.username },
                }"
                :disabled="loading"
                link
              >
                {{ t("labels.forgot_password") }}?
              </BaseButton>
              <BaseButton
                class="font-bold"
                name="submit-login"
                :disabled="loading || socialLoginLoading"
                full-width
              >
                {{ t("labels.sign_in") }}
              </BaseButton>
            </div>
          </UForm>

          <AuthProviderButtons
            :disabled="socialLoginLoading"
            @click-provider="executeSocialLogin"
          />
        </template>
      </div>
      <AuthTerms class="text-center" />
    </div>
  </BaseMain>
</template>
